// manifest.json builder: the document the desktop importer validates strictly.

import { APP_NAME, BUNDLE_FORMAT_VERSION } from "./constants";
import { entryNamesFor, planCells, type ManifestCell } from "./plan";
import { parseBundleInput, type BundleInput } from "./schema";

export interface Manifest {
  readonly format_version: typeof BUNDLE_FORMAT_VERSION;
  readonly app: typeof APP_NAME;
  readonly exported_at: string;
  readonly settings: {
    readonly volume: number;
    readonly language: string;
    readonly cell_size: string;
  };
  readonly cells: readonly ManifestCell[];
}

/** Desktop format: UTC ISO-8601 to whole seconds, e.g. "2026-10-19T12:00:00+00:00". */
export function formatExportedAt(date: Date): string {
  return `${date.toISOString().slice(0, 19)}+00:00`;
}

/** Build the manifest for validated input. `exportedAt` defaults to now (UTC). */
export function buildManifest(
  input: BundleInput,
  exportedAt: string = formatExportedAt(new Date()),
): Manifest {
  const valid = parseBundleInput(input);
  return {
    format_version: BUNDLE_FORMAT_VERSION,
    app: APP_NAME,
    exported_at: exportedAt,
    settings: {
      volume: valid.settings.volume,
      language: valid.settings.language,
      cell_size: valid.settings.cell_size,
    },
    cells: planCells(valid).map((cell) => cell.manifest),
  };
}

/** Zip entry names for a bundle: manifest.json first, then photos/ and songs/ entries. */
export function entryNames(input: BundleInput): readonly string[] {
  return entryNamesFor(planCells(parseBundleInput(input)));
}

export function manifestBytes(manifest: Manifest): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(manifest, null, 2));
}
